import { T, useT } from '@transifex/react';
import { Alert, Text, VStack } from '@vidahealth/vida-ui';
import { View } from 'react-native';

import type { Program } from '@/modules/api';

import { BehaviorGrid } from './components/BehaviorGrid';
import { BulletList } from './components/BulletList';
import { ProgramHeroImage } from './components/ProgramHeroImage';
import { useProgramBehaviors } from './hooks/useProgramBehaviors';
import { parseProgramDetail } from './utils/parseProgramDetail';

type ProgramOverviewProps = {
  program: Program;
  /**
   * Which coaching variant to present. `group` uses the program's
   * `groupDetailDescription` and the "Group Program" label; anything else (the
   * default) uses `detailDescription` and "Individual Program". Mirrors the
   * vida-design-system FullChoiceProgramReview demo.
   */
  modality?: 'individual' | 'group';
};

/**
 * Program detail content shared by the full-choice review and the primary
 * recommendation screens (mirrors stylos's ProgramOverviewPage and the
 * vida-design-system demo). The body is modality-aware: the program label, prose
 * paragraphs, and feature bullets all come from the individual vs. group detail
 * copy. Closes with the program's disclaimer as an acknowledgements alert.
 */
export function ProgramOverview({ program, modality = 'individual' }: ProgramOverviewProps) {
  const t = useT();

  const image = program.image || program.icon;
  const isGroup = modality === 'group';
  const detail =
    (isGroup && program.groupDetailDescription) ||
    program.detailDescription ||
    program.overviewDescription ||
    '';
  const { paragraphs, features } = parseProgramDetail(detail);
  const acknowledgments = parseProgramDetail(program.disclaimer).features;
  const programLabel = isGroup ? t('Group Program') : t('Individual Program');
  const behaviors = useProgramBehaviors(program.urn);

  return (
    <VStack space='xl'>
      {image ? <ProgramHeroImage uri={image} alt={program.title} /> : null}

      <Text size='xl' weight='semibold' className='text-typography-900'>
        {program.title}
      </Text>

      {program.description ? (
        <Text size='md' className='text-typography-900'>
          {program.description}
        </Text>
      ) : null}

      <Text size='md' weight='semibold' className='text-typography-900'>
        {programLabel}
      </Text>

      {paragraphs.map((paragraph) => (
        <Text key={paragraph} size='md' className='text-typography-900'>
          {paragraph}
        </Text>
      ))}

      {features.length > 0 ? <BulletList items={features} /> : null}

      {behaviors.length > 0 ? <BehaviorGrid behaviors={behaviors} /> : null}

      {acknowledgments.length > 0 ? (
        <Alert action='info' variant='solid'>
          <VStack space='sm' className='flex-1'>
            <Text size='md' weight='semibold' className='text-info-800'>
              <T _str='By continuing I acknowledge that:' />
            </Text>
            <View className='pl-2'>
              <BulletList items={acknowledgments} />
            </View>
          </VStack>
        </Alert>
      ) : null}
    </VStack>
  );
}
